<template>
  <div v-if="simulation">
    <menu-group class="itemheader">
      <h1 class="itemheader__title">
        {{ $t("labels.simulationSettings") }}
      </h1>
      <button
        class="button-icon itemheader__closebutton"
        type="button"
        @click="emit('close')"
      >
        <lucide-icon name="x" />
      </button>
    </menu-group>
    <drawer :title="$t('labels.general')" :open="true">
      <div class="column">
        <input-text
          :value="simulation.title"
          :label="$t('labels.title')"
          @update:value="updateSimulation({ title: $event })"
        />

        <input-texteditor
          :label="$t('labels.description')"
          :value="simulation.description"
          @update:value="updateSimulation({ description: $event })"
        />

        <section>
          <div class="label mt-2">
            {{ $t("objectNames.scenario") }}
          </div>
          <button-item v-if="scenario" @click="showScenarioInfo(scenario)">
            {{ scenario.title }}
          </button-item>
          <p v-else class="message mt-1 mb-2">
            {{ $t("messages.simulationHasNoScenario") }}
          </p>
        </section>
        <section v-if="scenario == null">
          <div class="line gap-2 items-end">
            <input-number
              v-model:value="maxRounds"
              :label="$t('labels.maxRounds')"
              :min="minSimulationRounds"
            />
            <button
              class="button pv-1"
              type="button"
              :disabled="maxRounds === simulation?.maxRounds"
              @click="saveNewMaxRounds()"
            >
              {{ $t("actions.save") }}
            </button>
          </div>
        </section>
      </div>
    </drawer>

    <drawer :title="$t('labels.simulationSettings')" :open="true">
      <div class="label">
        {{ $t("labels.simulationSpeed") }}
      </div>
      <input-range
        :min="0"
        :max="100"
        :value="simulationSpeed"
        @update:value="setSimulationSpeed($event)"
      >
        <template #before>
          <span>{{ $t("labels.slow") }}</span>
        </template>
        <template #after>
          <span>{{ $t("labels.fast") }}</span>
        </template>
      </input-range>

      <div class="list mt-2">
        <input-switch
          :label="$t('labels.showVisualisations')"
          :value="showVisualisations"
          @update:value="showVisualisations = $event"
        />
        <div v-show="showVisualisations">
          <input-switch
            :label="$t('labels.showIncidentMessages')"
            :value="showIncidentMessages"
            @update:value="showIncidentMessages = $event"
          />
          <input-switch
            :label="$t('labels.showActionMessages')"
            :value="showActionMessages"
            @update:value="showActionMessages = $event"
          />
          <input-switch
            :label="$t('labels.showMomentumMessages')"
            :value="showMomentumMessages"
            @update:value="showMomentumMessages = $event"
          />
          <input-switch
            :label="$t('labels.showConstantRelationMessages')"
            :value="showResourceMessages"
            @update:value="showResourceMessages = $event"
          />
          <input-switch
            :label="$t('labels.showActiveMarks')"
            :value="showActiveMarks"
            @update:value="showActiveMarks = $event"
          />
        </div>
      </div>
    </drawer>

    <drawer :title="$t('labels.simulationSettings')" :open="false">
      <input-switch
        :label="$t('labels.useScheduledEffects')"
        :value="useScheduledEffects"
        @update:value="useScheduledEffects = $event"
      />
    </drawer>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, computed } from "vue";
import Drawer from "@core/ui/DrawerBlock.vue";

import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputRange from "@core/input/InputRange.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import InputNumber from "@core/input/InputNumber.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";

const emit = defineEmits<{
  (e: "close"): void;
}>();

const simulationView = useSimulationViewStore();

const simulation = computed(() => simulationView.simulation);
const scenario = computed(() => simulationView.scenario);

// ===================
// Simulation Settings

const showIncidentMessages = computed({
  get() {
    return simulationView.showIncidentMessages;
  },
  set(value: boolean) {
    simulationView.setShowIncidentMessages(value);
  }
});

const showMomentumMessages = computed({
  get() {
    return simulationView.showMomentumMessages;
  },
  set(value: boolean) {
    simulationView.setShowMomentumMessages(value);
  }
});

const showResourceMessages = computed({
  get() {
    return simulationView.showResourceMessages;
  },
  set(value: boolean) {
    simulationView.set({
      showResourceMessages: value
    });
  }
});

const showActionMessages = computed({
  get() {
    return simulationView.showActionMessages;
  },
  set(value: boolean) {
    simulationView.setShowActionMessages(value);
  }
});

const showActiveMarks = computed({
  get() {
    return simulationView.showActiveMarks;
  },
  set(value: boolean) {
    simulationView.setShowActiveMarks(value);
  }
});

const showVisualisations = computed({
  get() {
    return simulationView.showVisualisations;
  },
  set(value: boolean) {
    simulationView.set({
      showVisualisations: value
    });
  }
});

const useScheduledEffects = computed({
  get() {
    return simulation.value?.useScheduledEffects == true;
  },
  set(value: boolean) {
    if (simulation.value == null) {
      throw new Error("No simulation");
    }

    useSimulationStore().update({
      id: simulation.value.id,
      useScheduledEffects: value
    });
    simulationView.updateSimulationInstance({
      useScheduledEffects: value
    });
  }
});

// ==========
// Max Rounds

const maxRounds = ref(0);

watch(() => simulation.value?.maxRounds, () => {
  maxRounds.value = simulation.value?.maxRounds ?? 0;
}, {
  immediate: true
});

const minSimulationRounds = computed(() => {
  const roundIndex = simulation.value?.currentRoundIndex ?? 0;
  return roundIndex + 1;
});

function saveNewMaxRounds() {
  console.log(maxRounds.value);
  simulationView.changeRoundAmount(maxRounds.value);
}

// ================
// Simulation Speed

const simulationSpeed = computed(() => Math.round((1 - simulationView.animationSpeed) * 100));

function setSimulationSpeed(speed: number) {
  simulationView.setAnimationSpeed(1 - Math.round(speed) / 100);
}

// ==========
// Extra

function updateSimulation(data = {}) {
  if (simulation.value == null) {
    throw new Error("No simulation");
  }

  useSimulationStore().update({
    id: simulation.value.id,
    ...data
  });
  simulationView.updateSimulationInstance(data);
}

function showScenarioInfo(scenario: { type: "scenario"; id: number }) {
  useOverlay().setItem(scenario);
}

</script>

<style lang="scss"></style>
